import {
  ArchiveIcon,
  CalendarDotsIcon,
  ChartLineUpIcon,
  CreditCardIcon,
  FilesIcon,
  GearIcon,
  GlobeHemisphereEastIcon,
  MountainsIcon,
  PlugIcon,
  ShieldCheckIcon,
  SwatchesIcon,
  UserCircleIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isPanelRoute } from '@/components/campaigns/plan/entry/opened-from';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { readReturnTo, rememberSettingsReturn } from '@/lib/settings/settings-return';
import { cn } from '@/lib/utils';

export interface SettingsNavItem {
  id:
    | 'profile'
    | 'archive'
    | 'brand-kit'
    | 'integrations'
    | 'automations'
    | 'team'
    | 'billing'
    | 'usage'
    | 'privacy'
    | 'competitors'
    | 'sources'
    | 'workspaces'
    | 'company';
  name: string;
  icon: Icon;
}

/**
 * Two labelled groups (2026-08-24, founder-requested — see incident): WORKSPACE
 * (the project = one brand, 1:1) → COMPANY (the billing tenant, which owns
 * one or more workspaces), with Profile living INSIDE Company as its first
 * row. Confirmed against Peec AI's nav shape.
 *
 * Profile used to render ungrouped, floating above both groups: "I think
 * there shouldn't be three things here" (founder) — an ungrouped item reads
 * as a third, unlabelled group. Profile is a personal/per-user page, but it
 * is the company's own login, so it nests under Company rather than getting
 * its own heading. `SETTINGS_NAV_GROUP_HEADING` is keyed by the nav item id
 * that STARTS each group, so the heading moved from `company` to `profile`
 * when profile became that group's first row — no second parallel array to
 * keep in sync.
 *
 * Brand / Voice / Audience were briefly three separate top-level nav rows
 * (2026-08-23-brand-and-context-redesign.md §7); undone the same day (§v5)
 * once two of the three held only 2-3 fields each — "does every element
 * earn its place" failed on a nav row holding two text fields. ONE Brand
 * Profile page now covers identity, voice, and audience — see BrandSettings.tsx.
 */
export const SETTINGS_NAV: ReadonlyArray<SettingsNavItem> = [
  // WORKSPACE — the project = one brand.
  { id: 'brand-kit', name: 'Brand profile', icon: SwatchesIcon },
  { id: 'competitors', name: 'Competitors', icon: MountainsIcon },
  { id: 'sources', name: 'Sources', icon: FilesIcon },
  { id: 'integrations', name: 'Connections', icon: PlugIcon },
  { id: 'automations', name: 'Automations', icon: CalendarDotsIcon },
  { id: 'archive', name: 'Archive', icon: ArchiveIcon },
  // COMPANY — the billing tenant. Profile is the personal/per-user login for
  // this company, listed first inside the group rather than floating above it.
  { id: 'profile', name: 'My profile', icon: UserCircleIcon },
  { id: 'company', name: 'Settings', icon: GearIcon },
  { id: 'workspaces', name: 'Workspaces', icon: GlobeHemisphereEastIcon },
  { id: 'team', name: 'Members', icon: UsersThreeIcon },
  { id: 'billing', name: 'Billing', icon: CreditCardIcon },
  { id: 'usage', name: 'Usage', icon: ChartLineUpIcon },
  { id: 'privacy', name: 'Faces', icon: ShieldCheckIcon },
] as const;

/**
 * Group headings for the settings nav, keyed by the nav item id that starts
 * each group. Exactly two groups — WORKSPACE and COMPANY — each render a
 * `SECTION_LABEL` eyebrow above their first row, with a hairline separator
 * before it (except the very first group). No ungrouped item.
 */
export const SETTINGS_NAV_GROUP_HEADING: Partial<Record<SettingsNavItem['id'], string>> = {
  'brand-kit': 'Workspace',
  profile: 'Company',
};

/**
 * Single source of truth for which Settings nav rows are visible. Shared by the
 * dialog nav filter AND its tests so the gate logic can't drift.
 *
 * Workspace-tenancy Phase 4: the 'organization' row (and its `orgAccess` gate)
 * is retired along with `organizations`/`locations`/`org_members` — franchise
 * hierarchy now lives on `brand_kits.parent_brand_kit_id` and is switched via
 * the workspace switcher, not a Settings CRUD panel. See
 * `docs/superpowers/specs/2026-08-23-workspace-tenancy-and-membership-design.md`
 * §3 (founder decision W6).
 */
export function settingsNavVisible(
  item: SettingsNavItem,
  opts: { teamEnabled: boolean; isAccountMember?: boolean },
): boolean {
  if (item.id === 'team') return opts.teamEnabled && !opts.isAccountMember;
  // Account members receive only the workspace grants assigned to them. They
  // may update their own Profile, but must not browse the tenant's Company,
  // Billing, Usage, or biometric-consent settings through Settings navigation.
  if (
    opts.isAccountMember &&
    ['company', 'workspaces', 'billing', 'usage', 'privacy'].includes(item.id)
  ) {
    return false;
  }
  return true;
}

export function settingsNavItemsForRole(
  role: 'owner' | 'member' | null,
  teamEnabled: boolean,
): SettingsNavItem[] {
  return SETTINGS_NAV.filter((item) =>
    settingsNavVisible(item, { teamEnabled, isAccountMember: role !== 'owner' }),
  );
}

export type SettingsSection = SettingsNavItem['id'];

// Company identity belongs to `accounts`: name + company_type are shared by all
// workspaces in the billing tenant. `brand_kits.name` is one workspace's brand.
// Role remains a personal profile field; company type is never read from the
// legacy `user_profiles.business_type` column.
// Brands & competitors was split (D21, 2026-07-17): competitors are owner-scoped
// and moved to their own Settings → Competitors row (they feed benchmark
// widgets, not brand identity — Analytics isn't a Mora noun, that section was
// renamed and split into Privacy + Competitors on 2026-08-18); followed brands
// are user-scoped and live entirely in Discover, where the rest of their code
// (brand-match.ts, feed.get.ts, DiscoverContextPanel) already is.
export type LegacySettingsSection =
  | 'business'
  | 'content-focus'
  | 'platforms'
  | 'brands-competitors'
  | 'scheduling'
  | 'engagement'
  | 'appearance'
  | 'brand'
  | 'voice-profile'
  | 'voice'
  | 'brand-voice'
  | 'brand-audience'
  | 'shopify'
  | 'auto-responder'
  | 'help';

export type AnySettingsSection = SettingsSection | LegacySettingsSection;

// Every brand-identity-flavored legacy alias now resolves to the single
// Brand page (2026-08-23-brand-and-context-redesign.md v5 undoes the
// three-way split from the same day — `brand-voice`/`brand-audience` are
// themselves now legacy ids, kept resolvable for anyone with an old
// deep-link or stored preference). `brands-competitors` is left pointing at
// its own dedicated `competitors` page — Competitors is a distinct feature
// (owner-scoped benchmark tracking), not a brand-identity field, and
// collapsing it into `brand-kit` would be a real regression, not a
// subtraction.
export const SECTION_ALIASES: Record<LegacySettingsSection, SettingsSection> = {
  business: 'brand-kit',
  'content-focus': 'brand-kit',
  platforms: 'integrations',
  'brands-competitors': 'competitors',
  scheduling: 'automations',
  engagement: 'integrations',
  appearance: 'profile',
  brand: 'brand-kit',
  'voice-profile': 'brand-kit',
  voice: 'brand-kit',
  'brand-voice': 'brand-kit',
  'brand-audience': 'brand-kit',
  shopify: 'integrations',
  'auto-responder': 'integrations',
  help: 'profile',
};

/**
 * What `mora:openSettings` may carry. A bare section id is the original and
 * still the common case; the object form lets a caller also name the platform
 * Settings should land on.
 */
export interface TargetedSettingsOpen {
  section?: AnySettingsSection;
  highlight?: string;
  accountId?: string;
}

export type OpenSettingsDetail = AnySettingsSection | TargetedSettingsOpen;

/**
 * A named predicate rather than an inline `typeof detail === 'object'`.
 * The inline form guarded the TRUE branch but left the false branch holding the
 * whole union, so `openSettings(detail ?? 'profile')` failed to compile with
 * `'{ section?…; highlight?… }' is not assignable to 'AnySettingsSection'`.
 * A predicate against a named interface narrows in both directions.
 */
function isTargetedOpen(detail: OpenSettingsDetail | undefined): detail is TargetedSettingsOpen {
  return typeof detail === 'object' && detail !== null;
}

export function resolveSection(value: string | null | undefined): SettingsSection {
  if (!value) return 'profile';
  if (SETTINGS_NAV.some((item) => item.id === value)) {
    return value as SettingsSection;
  }
  if (value in SECTION_ALIASES) {
    return SECTION_ALIASES[value as LegacySettingsSection];
  }
  return 'profile';
}

interface SettingsDialogContextValue {
  isOpen: boolean;
  activeSection: SettingsSection;
  /**
   * The platform a cross-route open asked Settings to land ON, when it named
   * one — e.g. `Reconnect Facebook` in the Insights scope menu.
   *
   * 🔴 It exists because the canonical `?reconnect=<platform>` deep link is
   * only readable after a real `navigate()`, and navigating to `/settings/*`
   * sets `previousPath` to a `/settings` path, which `performClose` then reads
   * as "deep-linked" and answers by dropping the reader on `/`. A remedy on
   * `/insights` that costs the reader `/insights` is a worse fix than the one
   * it replaced. So the platform rides the event instead, and
   * `IntegrationSettings` reads it with the URL params as its first choice —
   * one resolver, two inputs, never two highlight mechanisms.
   */
  highlightTarget: string | null;
  detailAccountId: string | null;
  openSettings: (section?: AnySettingsSection) => void;
  closeSettings: () => void;
  closeSettingsAndNavigate: (to: string) => void;
  setActiveSection: (section: AnySettingsSection) => void;
  setUnsavedChanges: (dirty: boolean, label?: string) => void;
}

const SettingsDialogContext = createContext<SettingsDialogContextValue | null>(null);

const NOOP_CONTEXT: SettingsDialogContextValue = {
  isOpen: false,
  activeSection: 'profile',
  highlightTarget: null,
  detailAccountId: null,
  openSettings: () => {},
  closeSettings: () => {},
  closeSettingsAndNavigate: () => {},
  setActiveSection: () => {},
  setUnsavedChanges: () => {},
};

export function useSettingsDialog() {
  const context = useContext(SettingsDialogContext);
  return context ?? NOOP_CONTEXT;
}

export function SettingsDialogProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSectionState] = useState<SettingsSection>('profile');
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const [highlightTarget, setHighlightTarget] = useState<string | null>(null);
  const [detailAccountId, setDetailAccountId] = useState<string | null>(null);
  const [unsavedChanges, setUnsavedChangesState] = useState({
    dirty: false,
    label: 'settings changes',
  });
  const [pendingAction, setPendingAction] = useState<
    | { kind: 'section'; section: SettingsSection }
    | { kind: 'close' }
    | { kind: 'navigate'; to: string }
    | null
  >(null);
  const location = useLocation();
  const navigate = useNavigate();

  const setUnsavedChanges = useCallback((dirty: boolean, label = 'settings changes') => {
    setUnsavedChangesState({ dirty, label });
  }, []);

  const openSettings = useCallback(
    (section: AnySettingsSection = 'profile') => {
      const resolved = resolveSection(section);
      if (isOpen && unsavedChanges.dirty && resolved !== activeSection) {
        setPendingAction({ kind: 'section', section: resolved });
        return;
      }
      if (!isOpen) {
        // The FULL previous address, not just its pathname — see the note on
        // `previousPath`'s restore in `performClose` below. A page carrying
        // `?w=<brandKitId>` must get that param back on close, not just its
        // route.
        setPreviousPath(`${location.pathname}${location.search}`);
        rememberSettingsReturn(
          isPanelRoute(location.pathname) ? `${location.pathname}${location.search}` : null,
        );
      }
      setActiveSectionState(resolved);
      setIsOpen(true);
      // Preserve the current query string (e.g. `?w=<brandKitId>`) rather than
      // replacing it outright. Settings has no workspace concept of its own —
      // it reads whichever brand kit is currently active — but the ADDRESS BAR
      // losing `?w=` here meant the id survived only in the workspace switch's
      // own localStorage pointer until the next hard load, so a copied link or
      // a back-navigation landed on the wrong workspace. See D5 (2026-09-03).
      window.history.replaceState(null, '', `/settings/${resolved}${location.search}`);
    },
    [activeSection, isOpen, location.pathname, location.search, unsavedChanges.dirty],
  );

  const performClose = useCallback(() => {
    setIsOpen(false);
    if (previousPath && !previousPath.startsWith('/settings')) {
      // Normal in-app open: React Router never left the underlying page
      // (openSettings only rewrote the URL via replaceState, which useLocation
      // can't see), so the route is still mounted behind the dialog. Restore
      // the URL in place — no navigation needed.
      window.history.replaceState(null, '', previousPath);
    } else {
      // Deep-linked / OAuth-return: React Router is genuinely on /settings/*,
      // where the route renders nothing but the dialog. We must navigate OFF
      // /settings or the /settings route effect immediately reopens the dialog.
      // navigate(-1) is unsafe here — the prior history entry is often another
      // /settings/* URL (e.g. the pre-OAuth connect screen), which reopens a
      // section instead of closing. Land on home: predictable, and it unmounts
      // the settings route so nothing can reopen it. A link that carried `?returnTo=` lands there instead.
      const returnTo = readReturnTo(location.search);
      if (returnTo) navigate(returnTo, { replace: true });
      else navigate('/');
    }
    rememberSettingsReturn(null);
    setPreviousPath(null);
    // A highlight is scoped to ONE open. Left set, the next unrelated open of
    // Settings would tint a row nobody asked about.
    setHighlightTarget(null);
    setDetailAccountId(null);
  }, [previousPath, navigate, location.search, setHighlightTarget, setDetailAccountId]);

  const closeSettings = useCallback(() => {
    if (unsavedChanges.dirty) {
      setPendingAction({ kind: 'close' });
      return;
    }
    performClose();
  }, [performClose, unsavedChanges.dirty]);

  const closeSettingsAndNavigate = useCallback(
    (to: string) => {
      if (unsavedChanges.dirty) {
        setPendingAction({ kind: 'navigate', to });
        return;
      }
      // Destination actions inside Settings must dismiss and navigate as one
      // operation. A separate close can race its deep-link navigate(-1).
      setIsOpen(false);
      setPreviousPath(null);
      rememberSettingsReturn(null);
      navigate(to);
    },
    [navigate, unsavedChanges.dirty],
  );

  const applyActiveSection = useCallback(
    (section: SettingsSection) => {
      setActiveSectionState(section);
      // Same `?w=` preservation as `openSettings` above — switching sections
      // while the dialog is already open must not drop it either. `location`
      // here is React Router's own, which `openSettings`'s replaceState calls
      // never touch (see the comment on that below), so it keeps reflecting
      // the real page's search for as long as Settings stays open.
      window.history.replaceState(null, '', `/settings/${section}${location.search}`);
    },
    [location.search],
  );

  const setActiveSection = useCallback(
    (section: AnySettingsSection) => {
      const resolved = resolveSection(section);
      if (unsavedChanges.dirty && resolved !== activeSection) {
        setPendingAction({ kind: 'section', section: resolved });
        return;
      }
      applyActiveSection(resolved);
    },
    [activeSection, applyActiveSection, unsavedChanges.dirty],
  );

  const discardAndContinue = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    setUnsavedChangesState({ dirty: false, label: 'settings changes' });
    if (!action) return;
    if (action.kind === 'section') applyActiveSection(action.section);
    else if (action.kind === 'navigate') {
      setIsOpen(false);
      setPreviousPath(null);
      navigate(action.to);
    } else {
      performClose();
    }
  }, [applyActiveSection, navigate, pendingAction, performClose]);

  // Cross-route event hook: any surface can dispatch `mora:openSettings` with an
  // optional `detail` (the section id) to open the settings dialog. The /billing
  // route uses this for its "Invite teammates" CTA so it can navigate the user
  // back to the app shell and open Settings → Team in one motion. Keeping this
  // here (rather than per-page) avoids each page wiring its own listener.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenSettingsDetail | undefined>).detail;
      // The object form is additive: every existing dispatcher passes a bare
      // section id and keeps working untouched. `resolveSection` would silently
      // answer `profile` for an object (`{} in SECTION_ALIASES` stringifies),
      // so the shape is split HERE rather than inside it.
      if (isTargetedOpen(detail)) {
        setHighlightTarget(detail.highlight ?? null);
        openSettings(detail.section ?? 'profile');
        return;
      }
      setHighlightTarget(null);
      openSettings(detail ?? 'profile');
    };
    window.addEventListener('mora:openSettings', handler);
    return () => window.removeEventListener('mora:openSettings', handler);
  }, [openSettings]);

  const value = useMemo<SettingsDialogContextValue>(
    () => ({
      isOpen,
      activeSection,
      highlightTarget,
      detailAccountId,
      openSettings,
      closeSettings,
      closeSettingsAndNavigate,
      setActiveSection,
      setUnsavedChanges,
    }),
    [
      isOpen,
      activeSection,
      highlightTarget,
      detailAccountId,
      openSettings,
      closeSettings,
      closeSettingsAndNavigate,
      setActiveSection,
      setUnsavedChanges,
    ],
  );

  return (
    <SettingsDialogContext.Provider value={value}>
      {children}
      <AlertDialog
        open={Boolean(pendingAction)}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved {unsavedChanges.label}</AlertDialogTitle>
            <AlertDialogDescription>
              Keep editing to save these changes, or discard them and continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(
                buttonVariants({ variant: 'neutral' }),
                'hover:text-neutral-foreground',
              )}
            >
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'outline' }), 'text-foreground')}
              onClick={discardAndContinue}
            >
              Discard and continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsDialogContext.Provider>
  );
}
